import Pagination, {
  PaginationRenderItemParams,
} from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";

import {
  forwardRef,
  ReactElement,
  ReactNode,
  useImperativeHandle,
} from "react";

import {
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiTextField,
  MuiTypography,
} from "..";

import Close from "@mui/icons-material/Close";

import { COMPONENT_IDS } from "../../helpers/constants/componentIds";

import { FormLabel, PaginationItem } from "@mui/material";
import { MuiStyledTooltip } from "../tooltip/MuiToolTip.style";
import MuiTableBody from "./components/MuiTableBody";
import MuiTableHeader from "./components/MuiTableHeader";
import { MuiTableWrapper } from "./MuiTable.style";
import { useMuiTableHook } from "./useMuiTableHook";
import classNames from "classnames";
export type RowValue = string | number | ReactNode;

export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  colSpan?: number;
  render?: (row: Record<string, RowValue>) => ReactNode;
  isSortVisible?: boolean;
  className?: string;
  objectKeyName?: string;
  headerRender?: () => ReactNode;
  isEmptyValue?: boolean;
  sortKeyName?: string;
  isAction?: boolean;
  considerSortAsString?: boolean;
}

export interface RowProps {
  [key: string]: any;
}

export interface TableProps {
  columns: Column[];
  tableResults: Record<string, RowValue>[];
  priorityResults?: Record<string, RowValue>[];
  tableHeaderForm: ReactElement[];
  pageId: string;
  noResultMessage?: string;
  noDataInApi: boolean;
  emptyMessage?: string;
  onRowSelected?: (row: RowProps) => void;
  footerColSpan?: number;
  emptyMessageColSpan?: number;
  isGroupView?: boolean;
  isFooterVisible?: boolean;
  isLastColumnSticky?: boolean;
  selectedId?: string;
  selectedIdKey?: string;
  rowIdKey?: string; // unique row field used to detect real result changes (default "id")
  isShowDashIfValueEmpty?: boolean;
}

export const ASC = "asc";
export const DESC = "desc";

export const MuiTable = forwardRef((props: TableProps, ref) => {
  const {
    commonLabels,
    commonARIALabel,
    tableResults,
    priorityResults,
    tableHeaderForm,
    columns,
    pageId,
    footerColSpan,
    emptyMessageColSpan,
    isGroupView,
    isFooterVisible,
    isLastColumnSticky,
    selectedId,
    selectedIdKey,
    isShowDashIfValueEmpty,
    page,
    rowsPerPage,
    sortColumn,
    sortDirection,
    filteredResults,
    showToolTip,
    errors,
    control,
    getValues,
    handleToolTipClose,
    isMobile,
    resetPagination,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRowSelected,
    handlePageChange,
    handleSort,
    goToPage,
    getColspan,
    getSiblingCount,
    getEmptyMessageAlignment,
    getEmptyMessage,
    tableContainerRef,
  } = useMuiTableHook(props);

  useImperativeHandle(ref, () => ({
    resetPagination: (shouldResetRowsPerPage: boolean = false) =>
      resetPagination(shouldResetRowsPerPage),
  }));

  const renderTableFooter = () => {
    const totalRecords = [...priorityResults, ...tableResults].length;
    const totalPages = Math.ceil(totalRecords / rowsPerPage);

    const renderPaginationItem = (item: PaginationRenderItemParams) => {
      const isNav = item.type === "previous" || item.type === "next";

      if (isNav) {
        return (
          <PaginationItem
            {...item}
            component="button"
            disabled={false}
            aria-disabled={item.disabled}
            className={item.disabled ? "Mui-disabled" : undefined}
          />
        );
      }

      return <PaginationItem {...item} />;
    };

    const renderGoToPageField = () => {
      const fieldProps = {
        id: `${pageId}-tablePaginationPageNo`,
        value: getValues("tablePaginationPageNo"),
        autoFocus: !!errors.tablePaginationPageNo,
        type: "number" as const,
        inputNumberWithoutDecimal: true,
        requiredARIALabel: commonARIALabel.required,
      };

      return (
        <MuiStyledTooltip
          placement="top"
          title={
            <>
              <Close onClick={handleToolTipClose} />
              <MuiTypography>
                {errors?.tablePaginationPageNo?.message}
              </MuiTypography>
            </>
          }
          open={showToolTip && Boolean(errors.tablePaginationPageNo)}
        >
          <MuiTextField
            {...fieldProps}
            className={classNames({
              "hide-error": errors.tablePaginationPageNo,
            })}
          />
        </MuiStyledTooltip>
      );
    };

    return (
      <TableFooter>
        <TableRow key="footer">
          {!isMobile && (
            <TablePagination
              id={`${pageId}-tablePagination`}
              rowsPerPageOptions={[10, 30, 50, 100]}
              colSpan={footerColSpan}
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": commonLabels.rowsPerPage,
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={commonLabels.rowsPerPage}
            />
          )}
          <TableCell colSpan={getColspan()}>
            <MuiBox className="goto-page-wrapper">
              <MuiBox className="pagination-container">
                <Pagination
                  count={totalPages}
                  shape="rounded"
                  page={page + 1}
                  onChange={handlePageChange}
                  siblingCount={getSiblingCount()}
                  boundaryCount={1}
                  {...(isMobile && { size: "small" })}
                  renderItem={renderPaginationItem}
                />
              </MuiBox>
              <MuiBox className="goto-controls">
                <FormLabel
                  htmlFor={`${pageId}-tablePaginationPageNo`}
                  sx={{ display: "contents" }}
                >
                  <MuiTypography variant="h3" id={`${pageId}-goto-page-label`}>
                    {commonLabels.goToPage}
                  </MuiTypography>
                </FormLabel>
                <FormController control={control} name="tablePaginationPageNo">
                  {renderGoToPageField()}
                </FormController>
                <MuiButtonSecondary
                  className="submit-goto-page"
                  aria-label={commonARIALabel.goToSelectedPage}
                  onClick={goToPage}
                >
                  {commonLabels.go}
                </MuiButtonSecondary>
              </MuiBox>
            </MuiBox>
          </TableCell>
        </TableRow>
      </TableFooter>
    );
  };

  const renderEmptyTableBody = () => {
    return (
      <TableBody id={`${pageId}-tableBody`}>
        <TableRow
          id={`${pageId}-tableRow`}
          key={`${pageId}-tableRow`}
          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
        >
          <TableCell
            component="th"
            scope="row"
            colSpan={emptyMessageColSpan}
            className="empty-table-cell"
          >
            <MuiBox className="empty-table-box">
              <MuiTypography
                variant="h3"
                align={getEmptyMessageAlignment()}
                data-testid={COMPONENT_IDS.COMMON_TABLE_NO_RESULT}
              >
                {getEmptyMessage()}
              </MuiTypography>
            </MuiBox>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  return (
    <MuiTableWrapper>
      <TableContainer
        component={Paper}
        id="table-container"
        sx={{ maxHeight: 700, minHeight: 100 }}
        ref={tableContainerRef}
      >
        <Table>
          <MuiTableHeader
            columns={columns}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            isLastColumnSticky={isLastColumnSticky}
            handleSort={handleSort}
            commonARIALabel={commonARIALabel}
            tableHeaderForm={tableHeaderForm}
          />

          {[...priorityResults, ...tableResults].length > 0 ? (
            <>
              <MuiTableBody
                pageId={pageId}
                rowsPerPage={rowsPerPage}
                filteredResults={filteredResults}
                page={page}
                isGroupView={isGroupView}
                selectedId={selectedId}
                selectedIdKey={selectedIdKey}
                handleRowSelected={handleRowSelected}
                columns={columns}
                isShowDashIfValueEmpty={isShowDashIfValueEmpty}
                isLastColumnSticky={isLastColumnSticky}
                commonARIALabel={commonARIALabel}
              />
              {isFooterVisible && !isMobile && renderTableFooter()}
            </>
          ) : (
            renderEmptyTableBody()
          )}
        </Table>
      </TableContainer>
      {isFooterVisible && isMobile && (
        <div className="mobile-footer">{renderTableFooter()}</div>
      )}
    </MuiTableWrapper>
  );
});
